"use client";

import { useEffect, useMemo, useState } from "react";
import { getAvailableSubscriptions } from "../botb-pass/subscription-service";
import { Context } from "./Context";
import { CompetitionPrizeTypes, CompetitionsResponse, SubscriptionResponse } from "./types";
import {
  cancelSubscription,
  getListOfInvoices,
  getUserSubscriptionPlan,
  reactivateSubscription,
} from "../subscription/subscription-service";
import { getCookie } from "../../../helpers/cookies";

export default function ManagementSubscriptionContext({ children }: { children: React.ReactNode }) {
  const [isSubscriptionLoading, setIsSubscriptionLoading] = useState(true);
  const [isInvoicesLoading, setIsInvoicesLoading] = useState(true);
  const [allPackages, setAllPackages] = useState<Package[]>([]);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [subscriptionPlan, setSubscriptionPlan] = useState<SubscriptionPlanUser>({
    amount: 0,
    cancel_at_period_end: false,
    currency: "",
    current_period_end: 1760517034,
    current_period_start: 0,
    description: "",
    features: [],
    id: "",
    interval: "",
    intervalCount: 0,
    metadata: [],
    name: "",
    prices: [],
    productId: "",
    status: "",
  });
  const [invoicesList, setInvoicesList] = useState<Invoice[]>([]);
  const [shouldShowCancelSubscriptionStep, setShouldShowCancelSubscriptionStep] = useState(false);
  const subsWillBeCancelled = useMemo(() => subscriptionPlan.cancel_at_period_end === true, [subscriptionPlan]);
  const activePricePlan = useMemo<SubscriptionPriceUser>(
    () =>
      subscriptionPlan.prices.find(price => price.active) || {
        active: false,
        billing_scheme: "",
        created: 0,
        currency: "",
        custom_unit_amount: 0,
        id: "",
        livemode: false,
        lookup_key: "",
        metadata: { base_price: "", discount_value_card: "", upsell_discount_value_switch: "" },
        nickname: "",
        object: "",
        product: undefined,
        recurring: { interval: "", interval_count: 0, meter: undefined, trial_period_days: undefined, usage_type: "" },
        tax_behavior: "",
        tiers_mode: "",
        transform_quantity: undefined,
        type: "",
        unit_amount: 0,
        unit_amount_decimal: "",
      },
    [subscriptionPlan]
  );
  const [botbpassCompetitions, setBotbpassCompetitions] = useState<CompetitionsResponse>({
    played: [],
    lost: [],
  });
  const [cancelProcessActiveStep, setCancelProcessActiveStep] = useState<number>(1);
  const [isInvoiceProcessing, setIsInvoiceProcessing] = useState(false);
  const [straplineTexts, setStraplineTexts] = useState<CompetitionPrizeTypes>({
    PrizeStraplineText1: "",
    PrizeStraplineText2: "",
    PrizeStraplineText3: "",
  });
  useEffect(() => {
    const isAuth = getCookie("BotB.Authenticated");
    if (isAuth !== "true") {
      location.href = `/login?ReturnUrl=${encodeURIComponent(location.pathname + location.search)}`;
      return;
    }
    getUserSubscriptionPlan()
      .then(res => res.json())
      .then(res => {
        if (!res) {
          location.href = "/botb-pass";
          return;
        }
        setSubscriptionPlan(res);
        setIsSubscriptionLoading(false);
      })
      .catch(err => {
        console.error(err);
        location.href = "/botb-pass";
      });
    getListOfInvoices()
      .then(res => res.json())
      .then((res: InvoiceList) => {
        if (res.data) {
          setInvoicesList(res.data);
        }
        setIsInvoicesLoading(false);
      })
      .catch(err => console.error(err));
    getAvailableSubscriptions()
      .then(res => res.json())
      .then((res: Package[]) => {
        if (Array.isArray(res)) {
          setAllPackages(res);
        }
      })
      .catch(err => console.error(err));
  }, []);

  const onNextModal = () => {
    setCancelProcessActiveStep(prevState => prevState + 1);
  };

  const onPrevModal = () => {
    setCancelProcessActiveStep(prevState => prevState - 1);
  };

  const onCloseCancelSubscriptionStep = () => {
    setShouldShowCancelSubscriptionStep(false);
    setCancelProcessActiveStep(1);
    location.href = "/";
  };

  const onRenewSubscription = () => {
    setIsButtonDisabled(true);
    reactivateSubscription()
      .then(res => res.json())
      .then((res: SubscriptionResponse) => {
        setSubscriptionPlan(prevSubscriptionPlan => ({
          ...prevSubscriptionPlan,
          cancel_at_period_end: res.cancel_at_period_end,
        }));
      })
      .catch(err => {
        console.error(err);
      })
      .finally(() => {
        setIsButtonDisabled(false);
        setShouldShowCancelSubscriptionStep(false);
        setCancelProcessActiveStep(1);
      });
  };

  const onCancelSubscription = () => {
    setIsButtonDisabled(true);
    cancelSubscription()
      .then(res => res.json())
      .then((res: SubscriptionResponse) => {
        setSubscriptionPlan(prevSubscriptionPlan => ({
          ...prevSubscriptionPlan,
          cancel_at_period_end: res.cancel_at_period_end,
        }));
      })
      .catch(err => {
        console.error(err);
      })
      .finally(() => {
        setIsButtonDisabled(false);
        setShouldShowCancelSubscriptionStep(false);
        setCancelProcessActiveStep(1);
      });
  };

  return (
    <Context.Provider
      value={{
        isSubscriptionLoading,
        isInvoicesLoading,
        allPackages,
        isButtonDisabled,
        setIsButtonDisabled,
        subscriptionPlan,
        setSubscriptionPlan,
        invoicesList,
        setInvoicesList,
        shouldShowCancelSubscriptionStep,
        setShouldShowCancelSubscriptionStep,
        onCancelSubscriptionModal: onCloseCancelSubscriptionStep,
        subsWillBeCancelled,
        activePricePlan,
        // @ts-ignore
        totalEntries:
          subscriptionPlan.metadata.length > 0 && Object.fromEntries(subscriptionPlan.metadata)["total_entries"],
        // @ts-ignore
        basePrice: subscriptionPlan.prices?.[0]?.metadata["base_price"],
        botbPassCompetitions: botbpassCompetitions,
        setBotbpassCompetitions,
        cancelProcessActiveStep,
        onNext: onNextModal,
        onOnPrevious: onPrevModal,
        straplineTexts,
        setStraplineTexts,
        onRenewSubscription,
        onCancelSubscription,
        isInvoiceProcessing,
        setIsInvoiceProcessing,
      }}>
      {children}
    </Context.Provider>
  );
}
